import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import ApiService from "../../Core/Service/ApiService";
import LoadingIndicator from "../../Components/LoadingIndicator/LoadingIndicator";
import { MDBBtn, MDBCard, MDBCardBody, MDBCardHeader, MDBCardTitle, MDBCheckbox, MDBCol, MDBIcon, MDBInput, MDBRow, MDBSpinner, MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import { useSelector } from "react-redux";
import { memoizedGetAuthUser } from "../../Store/Reducer/authReducer";
import { useContext } from "react";
import CmsContext from "../../Context/CmsContext";
import Media from "../../Components/CMS/Media";
import ColorPicker from "../../Components/CMS/ColorPicker";
import { CKEditor } from 'ckeditor4-react';
import { toast } from "react-toastify";

const SplashConfig = React.memo(props => {
    const cmsContext = useContext(CmsContext);
    const authUser = useSelector(memoizedGetAuthUser);
    const [formData, setFormData] = useState({
        style: {
            bg_color: '#555',
            txt_color: '#fff',
        },
        logo_web: {
            type: 'image',
            upload_file: '',
            external_link: '',
            active_link: 'external'
        },
        logo_mobile: {
            type: 'image',
            upload_file: '',
            external_link: '',
            active_link: 'external'
        },
        content_html: '',
        column1: {
            header: {
                content: 'Quick Link',
                txt_color: '#fff',
            },
            links: []
        },
        column2: {
            header: {
                content: 'Help & Policy',
                txt_color: '#fff',
            },
            links: []
        },
        bottom_html: ''
    });
    const formDataRef = useRef(formData);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        cmsContext.setBreadcrumb([
            { title: 'Site Config'},
            { title: 'Splash'},
        ])

        setLoading(true);
        ApiService.get('/siteconfig/splash').then(response => {
            const row = response.data.row;
            if(row) {
                let data = {
                    ...formData,
                    ...row,
                    style: row.style ? JSON.parse(row.style) : formData.style,
                    logo_web: row.logo_web ? JSON.parse(row.logo_web) : formData.logo_web,
                    logo_mobile: row.logo_mobile ? JSON.parse(row.logo_mobile) : formData.logo_mobile,
                    column1: row.column1 ? JSON.parse(row.column1) : formData.column1,
                    column2: row.column2 ? JSON.parse(row.column2) : formData.column2,
                    bottom: row.bottom ? JSON.parse(row.bottom) : formData.bottom,
                }
    
                setFormData(data);
            }
            
            setLoaded(true);
        }).finally(() => {
            setLoading(false);
        })
    }, []);

    useEffect(() => {
        formDataRef.current = {...formData};
    }, [formData]);

    const handleSave = () => {
        const data = {
            ...formData,
            style: JSON.stringify(formData.style),
            logo_web: JSON.stringify(formData.logo_web),
            logo_mobile: JSON.stringify(formData.logo_mobile),
            column1: JSON.stringify(formData.column1),
            column2: JSON.stringify(formData.column2),
        };

        setSaving(true);
        ApiService.post('/siteconfig/splash', data).then(response=>{
            toast.success(response.data.message);
        }).finally(() => {
            setSaving(false);
        })
    }

    const splashStyle = () => {
        return (
        <MDBCard className="splash-style">
            <MDBCardHeader>
                <MDBCardTitle>Background</MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
                <div className="d-flex">
                    <ColorPicker
                        label="Background Color:"
                        color={formData.style.bg_color}
                        setColor={(color) => setFormData({
                            ...formData,
                            style: {
                                ...formData.style,
                                bg_color: color
                            }
                        })}
                    />
                    <ColorPicker
                        className='ms-4'
                        label="Text Color:"
                        color={formData.style.txt_color}
                        setColor={(color) => setFormData({
                            ...formData,
                            style: {
                                ...formData.style,
                                txt_color: color
                            }
                        })}
                    />
                </div>
            </MDBCardBody>
        </MDBCard>
        )
    }

    const splashLogo = () => {
        return (
            <Media label='Logo' formData={formData} setFormData={setFormData} webField='logo_web' mobileField='logo_mobile' />
        )
    }

    const splashContent = () => {
        return (
        <MDBCard className="splash-text mt-4">
            <MDBCardHeader>
                <MDBCardTitle>Splash Text</MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
                {loaded && (
                    <CKEditor
                        initData={formData.content_html}
                        editorUrl="/vendor/ckeditor/ckeditor.js"
                        onInstanceReady={ () => {} }
                        onChange={e => {
                            setFormData({
                                ...formDataRef.current,
                                content_html: e.editor.getData()
                            })
                        }}
                    />
                )}
            </MDBCardBody>
        </MDBCard>
        )
    }

    const splashLinks = (column, label) => {
        return (
        <MDBCard className="splash-column-links mt-4">
            <MDBCardHeader>
                <MDBCardTitle>{ label }</MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
                <div className="header">
                    <h6>Header:</h6>
                    <div className="d-flex align-items-center">
                        <div>
                            <MDBInput 
                                value={formData[`${column}`].header.content} 
                                onChange={e=>{
                                    const fd = {...formData};
                                    fd[`${column}`].header.content = e.target.value;
                                    setFormData(fd);
                                }}
                            />
                        </div>
                        <ColorPicker
                            className='ms-4'
                            label="Text Color:"
                            color={formData[`${column}`].header.txt_color} 
                            setColor={(color) => {
                                const fd = {...formData};
                                fd[`${column}`].header.txt_color = color;
                                setFormData(fd);
                            }}
                        />
                    </div>
                </div>
                <div className="links mt-4">
                    <h6>Links:</h6>
                    <div className="d-flex justify-content-end mt-2">
                        <MDBBtn className="py-1 px-4" outline onClick={e=>{
                            const fd = {...formData};
                            fd[`${column}`].links.push({
                                label: '',
                                url: ''
                            })
                            setFormData(fd);
                        }}>Add</MDBBtn>
                    </div>
                    <MDBTable className="mt-2">
                        <MDBTableHead>
                            <tr>
                                <th scope="col">Label</th>
                                <th scope="col">Link</th>
                                <th scope="col">Action</th>
                            </tr>
                        </MDBTableHead>
                        <MDBTableBody>
                            {formData[`${column}`].links.map((link, index) => (
                                <tr key={index}>
                                    <td>
                                        <MDBInput 
                                            value={link.label}
                                            onChange={e=>{
                                                const fd = {...formData};
                                                fd[`${column}`].links[index].label = e.target.value;
                                                setFormData(fd);
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <MDBInput
                                            value={link.url}
                                            onChange={e=>{
                                                const fd = {...formData};
                                                fd[`${column}`].links[index].url = e.target.value;
                                                setFormData(fd);
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <MDBBtn color="none" tag="a" className="ms-1"
                                            onClick={e=>{
                                                const fd = {...formData};
                                                fd[`${column}`].links.splice(index, 1);
                                                setFormData(fd);
                                            }}
                                        >
                                            <MDBIcon far icon="trash-alt" size="lg" color="danger" />
                                        </MDBBtn>
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td></td>
                                <td colSpan={2}><p>Note: If you want to link to a different domain, <br/>please input like https://example.com</p></td>
                            </tr>
                        </MDBTableBody>
                    </MDBTable>
                </div>
            </MDBCardBody>
        </MDBCard>
        )
    }

    const splashBottom = () => {
        return (
        <MDBCard className="splash-bottom mt-4">
            <MDBCardHeader>
                <MDBCardTitle>Bottom</MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
                {loaded && (
                    <CKEditor
                        initData={formData.bottom_html}
                        editorUrl="/vendor/ckeditor/ckeditor.js"
                        onInstanceReady={ () => {} }
                        onChange={e => {
                            setFormData({
                                ...formDataRef.current,
                                bottom_html: e.editor.getData()
                            })
                        }}
                    />
                )}
            </MDBCardBody>
        </MDBCard>
        )
    }

    return (
    <div className="splash-config">
        { splashStyle() }
        <div className="d-flex">
            <div className="flex-fluid">
                { splashLogo() }
                { splashContent() }
                { splashBottom() }
            </div>
            <div className="flex-fluid ms-4">
                { splashLinks('column1', 'Column1') }
                { splashLinks('column2', 'Column2') }
            </div>
        </div>

        <div className="d-flex justify-content-end mt-4">
            <MDBBtn onClick={handleSave}>
                {saving && <MDBSpinner size="sm" className="me-1" />}
                Save
            </MDBBtn>
        </div>

        <LoadingIndicator show={loading} fullScreen={true} />
    </div>
    )
})

export default SplashConfig;